import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer,
} from '@mui/material';
import { UserContext } from '../../../components/shared/useUser';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import CustomPagination from '../../../components/forms/Pagination';
import { client } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../dialogs/CloseDialogAction';

interface OrderAuditModalProps {
  currentOrderId: number;
  open: boolean;
  onClose: () => void;
  isOfd?: boolean;
}

const OrderAuditModal: React.FC<OrderAuditModalProps> = ({ currentOrderId, open, onClose, isOfd }) => {

  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('orderDetails');

  const [loading, setLoading] = useState(false);

  const [headers, setHeaders] = useState<string[]>([]);
  const [values, setValues] = useState<string[][]>([]);

  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (open) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedStore]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await client.getOrderAudit(selectedStore?.storeNumber, currentOrderId);

      let headers: string[] = [];
      let prevValue: string = '';

      for (let i = 0; i < response.data.length; i++) {
        if (prevValue.startsWith('<th')) {
          headers.push(response.data[i] ?? '');
        }
        prevValue = response.data[i] ?? '';
      }

      let rows: string[][] = [];
      let row: string[] = [];
      prevValue = '';

      for (let i = 0; i < response.data.length; i++) {
        if (prevValue.startsWith('<td')) {
          row.push((response.data[i] ?? '').replace(/<\/tr>/g, '\n').replace(/<[/a-zA-Z0-9\-" ;#:=]*>/g, ' '));
        }
        if (row.length === headers.length) {
          rows.push(row);
          row = [];
        }
        prevValue = response.data[i] ?? '';
      }

      if (isOfd && headers.length > 0) {
        // Remove 'ClientIP' header and column
        const clientIpIdx = headers.findIndex(h => h.trim().toLowerCase() === 'clientip');
        if (clientIpIdx !== -1) {
          headers = headers.filter((_, idx) => idx !== clientIpIdx);
          rows = rows.map(r => r.filter((_, idx) => idx !== clientIpIdx));
        }

        const clientIdx = headers.findIndex(h => h.trim().toLowerCase() === 'client');
        const emailIdx = headers.findIndex(h => h.trim().toLowerCase() === 'emailaddress');
        if (clientIdx !== -1) {
          for (const r of rows) {
            r[clientIdx] = 'OFD';
          }
        }
        // Only keep rows that have a non-empty email field + is a valid email
        if (emailIdx !== -1) {
          const filteredRows = rows.filter(r => r[emailIdx] && r[emailIdx].trim().includes('@'));
          setHeaders(headers);
          setValues(filteredRows);
          setTotalPages(Math.ceil(filteredRows.length / pageSize));
          setPage(0);
          setLoading(false);
          return;
        }
      }

      setHeaders(headers);
      setValues(rows);
      setTotalPages(Math.ceil(rows.length / pageSize));
      setPage(0);
    } catch (error) {
      console.error('Error fetching audit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    onClose();
  };

  return (
    <Dialog open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>
        {t('orderAudit')}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingWheel />
        ) : (
          <TableContainer component={'div'}>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map(h => <TableCell key={h}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {values.slice(pageSize * page, pageSize * page + pageSize).map((r, idx) => (
                  <TableRow key={idx}>
                    {r.map((value, idx) => (
                      <TableCell key={idx}>
                        {value.split('\n').map((x, idx, arr) => <React.Fragment key={idx}>{x}{idx < arr.length - 1 ? <br /> : null}</React.Fragment>)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={totalPages}
              breakpointForChangeDisplay={120000}
            />
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderAuditModal;